import { useEffect, useId, useState, type FormEvent } from "react";
import type { SlugDePapelContrato, UsuarioDoPainel } from "@base/contracts";
import {
  MoreHorizontal,
  Pencil,
  Search,
  UserPlus,
  UserRound,
  UserX,
} from "lucide-react";
import { CampoDeTexto, EstadoVazio, MolduraDoApp } from "@base/ui";
import { Button as BotaoRenke, Modal } from "@base/ui/renke";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { PageLayout } from "@/components/layout/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import {
  useAtualizarStatusUsuario,
  useAtualizarUsuario,
  useCriarUsuario,
  useListarPapeis,
  useListarUsuarios,
  usePodeVerUsuarios,
  useTemGerirUsuarios,
} from "../application/use-usuarios";

type FiltroStatus = "ativo" | "inativo" | "todos";

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "U";
  if (partes.length === 1) return (partes[0]?.slice(0, 1) ?? "U").toUpperCase();
  return `${partes[0]?.slice(0, 1) ?? ""}${partes[partes.length - 1]?.slice(0, 1) ?? ""}`.toUpperCase();
}

/**
 * Gestao de membros do projeto ativo (menu da Conta).
 * Padrao Next-Elite: Card + Table (como Clientes).
 */
export function PaginaAdminUsuarios() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("admin-usuarios");
  const podeVer = usePodeVerUsuarios();
  const podeEscrever = useTemGerirUsuarios();
  const { sessao } = useAuth();
  const [busca, definirBusca] = useState("");
  const [filtro, definirFiltro] = useState<FiltroStatus>("ativo");
  const [q, definirQ] = useState("");
  const [modalAberto, definirModalAberto] = useState(false);
  const [editando, definirEditando] = useState<UsuarioDoPainel | null>(null);

  const lista = useListarUsuarios({
    ...(q ? { q } : {}),
    status: filtro,
  });
  const alterarStatus = useAtualizarStatusUsuario();

  useEffect(() => {
    const timer = window.setTimeout(() => definirQ(busca.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [busca]);

  const nomeProjeto = sessao?.cliente?.trim() || "projeto ativo";
  const itens = lista.data?.itens ?? [];

  if (!podeVer) {
    return (
      <MolduraDoApp
        nomeDoApp="Creative OS"
        marca={<LogoRenkeRefs />}
        titulo="Usuários"
        subtitulo="Acesso restrito"
        navegacao={navegacao}
        rodapeDaLateral={rodapeDaSessaoNaLateral}
        {...seletor}
      >
        <Alert variant="destructive">
          <AlertTitle>Sem permissão</AlertTitle>
          <AlertDescription>
            Você precisa de permissão usuarios:ler na conta.
          </AlertDescription>
        </Alert>
      </MolduraDoApp>
    );
  }

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Usuários"
      subtitulo={`Membros de ${nomeProjeto}`}
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
      acoes={
        podeEscrever ? (
          <BotaoRenke
            type="button"
            variante="brand"
            onClick={() => {
              definirEditando(null);
              definirModalAberto(true);
            }}
          >
            <UserPlus className="size-4" aria-hidden />
            Novo usuário
          </BotaoRenke>
        ) : undefined
      }
    >
      <PageLayout className="animate-aparecer space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
            <CardDescription>
              Busque por nome, e-mail ou papel e filtre pelo status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <CampoDeTexto
                  rotulo="Buscar"
                  value={busca}
                  onChange={(e) => definirBusca(e.target.value)}
                  placeholder="Nome, e-mail ou papel"
                  icone={<Search className="size-4" aria-hidden />}
                />
              </div>
              <div className="grid w-full gap-1.5 sm:w-44">
                <Label htmlFor="filtro-status-usuarios">Status</Label>
                <Select
                  value={filtro}
                  onValueChange={(valor) => {
                    if (
                      valor === "ativo" ||
                      valor === "inativo" ||
                      valor === "todos"
                    ) {
                      definirFiltro(valor);
                    }
                  }}
                >
                  <SelectTrigger
                    id="filtro-status-usuarios"
                    className="h-10 cursor-pointer"
                    aria-label="Filtrar por status"
                  >
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativos</SelectItem>
                    <SelectItem value="inativo">Inativos</SelectItem>
                    <SelectItem value="todos">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lista</CardTitle>
            <CardDescription>
              {lista.isPending
                ? "Carregando…"
                : `${itens.length} usuário${itens.length === 1 ? "" : "s"}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 pb-2">
            {lista.isPending ? (
              <div className="space-y-3 px-6">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : null}

            {lista.isError ? (
              <div className="px-6">
                <Alert variant="destructive">
                  <AlertTitle>Falha ao carregar</AlertTitle>
                  <AlertDescription>
                    {lista.error instanceof ErroDaApi
                      ? lista.error.message
                      : "Não foi possível carregar os usuários."}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}

            {!lista.isPending && !lista.isError && itens.length === 0 ? (
              <div className="px-6 py-4">
                <EstadoVazio
                  icone={<UserRound className="size-8" aria-hidden />}
                  titulo="Nenhum usuário neste filtro"
                  descricao={
                    podeEscrever
                      ? "Ajuste a busca ou convide alguém para este projeto."
                      : "Ajuste a busca ou o status e tente de novo."
                  }
                  acao={
                    podeEscrever ? (
                      <BotaoRenke
                        type="button"
                        variante="brand"
                        tamanho="sm"
                        onClick={() => {
                          definirEditando(null);
                          definirModalAberto(true);
                        }}
                      >
                        Novo usuário
                      </BotaoRenke>
                    ) : undefined
                  }
                />
              </div>
            ) : null}

            {!lista.isPending && !lista.isError && itens.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Usuário</TableHead>
                      <TableHead>Papel</TableHead>
                      <TableHead>Status</TableHead>
                      {podeEscrever ? (
                        <TableHead className="pr-6 text-right">Ações</TableHead>
                      ) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itens.map((usuario) => {
                      const souEu = sessao?.email === usuario.email;
                      return (
                        <TableRow key={usuario.id}>
                          <TableCell className="pl-6">
                            <div className="flex min-w-0 items-center gap-3">
                              <Avatar className="size-9">
                                <AvatarFallback className="text-xs font-semibold">
                                  {iniciais(usuario.nome)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="truncate font-medium text-foreground">
                                  {usuario.nome}
                                  {souEu ? (
                                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                                      você
                                    </span>
                                  ) : null}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {usuario.email}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-normal">
                              {usuario.papel.nome}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                usuario.ativo ? "success" : "outline"
                              }
                            >
                              {usuario.ativo ? "Ativo" : "Inativo"}
                            </Badge>
                          </TableCell>
                          {podeEscrever ? (
                            <TableCell className="pr-6 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-8 cursor-pointer"
                                    aria-label={`Ações de ${usuario.nome}`}
                                  >
                                    <MoreHorizontal
                                      className="size-4"
                                      aria-hidden
                                    />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="z-[600]"
                                >
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => {
                                      definirEditando(usuario);
                                      definirModalAberto(true);
                                    }}
                                  >
                                    <Pencil className="size-4" aria-hidden />
                                    Editar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    disabled={
                                      (souEu && usuario.ativo) ||
                                      alterarStatus.isPending
                                    }
                                    onClick={() =>
                                      void alterarStatus.mutateAsync({
                                        id: usuario.id,
                                        corpo: { ativo: !usuario.ativo },
                                      })
                                    }
                                  >
                                    <UserX className="size-4" aria-hidden />
                                    {usuario.ativo ? "Desativar" : "Reativar"}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <ModalUsuario
          aberto={modalAberto}
          usuario={editando}
          aoFechar={() => {
            definirModalAberto(false);
            definirEditando(null);
          }}
        />
      </PageLayout>
    </MolduraDoApp>
  );
}

/** Modal de criar ou editar membro do workspace. */
function ModalUsuario({
  aberto,
  usuario,
  aoFechar,
}: {
  readonly aberto: boolean;
  readonly usuario: UsuarioDoPainel | null;
  readonly aoFechar: () => void;
}) {
  const idForm = useId();
  const idPapel = useId();
  const papeis = useListarPapeis(aberto);
  const criar = useCriarUsuario();
  const atualizar = useAtualizarUsuario();
  const [nome, definirNome] = useState("");
  const [email, definirEmail] = useState("");
  const [senha, definirSenha] = useState("");
  const [papelSlug, definirPapelSlug] =
    useState<SlugDePapelContrato>("creator");
  const [erro, definirErro] = useState<string | null>(null);

  const editando = usuario !== null;
  const carregando = criar.isPending || atualizar.isPending;

  useEffect(() => {
    if (!aberto) return;
    definirNome(usuario?.nome ?? "");
    definirEmail(usuario?.email ?? "");
    definirSenha("");
    definirPapelSlug(usuario?.papel.slug ?? "creator");
    definirErro(null);
  }, [aberto, usuario]);

  function fecharComSeguranca() {
    if (carregando) return;
    aoFechar();
  }

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    try {
      if (editando) {
        await atualizar.mutateAsync({
          id: usuario.id,
          corpo: {
            nome,
            papelSlug,
            ...(senha.trim().length >= 8 ? { senha: senha.trim() } : {}),
          },
        });
      } else {
        await criar.mutateAsync({
          nome,
          email,
          senha,
          papelSlug,
        });
      }
      aoFechar();
    } catch (causa) {
      definirErro(
        causa instanceof ErroDaApi
          ? causa.message
          : causa instanceof Error
            ? causa.message
            : "Não foi possível salvar o usuário.",
      );
    }
  }

  return (
    <Modal
      aberto={aberto}
      titulo={editando ? "Editar usuário" : "Novo usuário"}
      descricao={
        editando
          ? "Atualize nome, papel ou senha deste membro."
          : "Cria o acesso e vincula ao projeto atual."
      }
      onFechar={fecharComSeguranca}
      mostrarFechar={false}
      className="max-w-lg"
      rodape={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BotaoRenke
            type="button"
            variante="secondary"
            tamanho="sm"
            onClick={fecharComSeguranca}
            disabled={carregando}
          >
            Cancelar
          </BotaoRenke>
          <BotaoRenke
            type="submit"
            form={idForm}
            variante="brand"
            tamanho="sm"
            carregando={carregando}
            disabled={carregando}
          >
            {editando ? "Salvar" : "Criar usuário"}
          </BotaoRenke>
        </div>
      }
    >
      <form
        id={idForm}
        className="mt-4 space-y-4"
        onSubmit={(e) => void aoEnviar(e)}
      >
        <CampoDeTexto
          rotulo="Nome"
          value={nome}
          onChange={(e) => definirNome(e.target.value)}
          required
          autoComplete="name"
          autoFocus
        />
        {!editando ? (
          <CampoDeTexto
            rotulo="E-mail"
            type="email"
            value={email}
            onChange={(e) => definirEmail(e.target.value)}
            required
            autoComplete="username"
          />
        ) : null}
        <div className="grid gap-1.5">
          <Label htmlFor={idPapel}>Papel</Label>
          <Select
            value={papelSlug}
            onValueChange={(valor) =>
              definirPapelSlug(valor as SlugDePapelContrato)
            }
          >
            <SelectTrigger id={idPapel} className="h-11 cursor-pointer">
              <SelectValue placeholder="Escolher papel" />
            </SelectTrigger>
            <SelectContent>
              {(papeis.data?.itens ?? []).map((papel) => (
                <SelectItem key={papel.id} value={papel.slug}>
                  {papel.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <CampoDeTexto
          rotulo={editando ? "Nova senha (opcional)" : "Senha inicial"}
          type="password"
          value={senha}
          onChange={(e) => definirSenha(e.target.value)}
          required={!editando}
          autoComplete="new-password"
        />
        {erro ? (
          <p className="text-sm text-destructive" role="alert">
            {erro}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
